import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, CalendarCheck } from "lucide-react";
import { BRAND, NAV_LINKS, SOCIALS } from "@/data/site";
import BottomSheet from "@/components/BottomSheet";

const Navbar = ({ light = false }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 450);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(href);
    }
  };

  const tone = light
    ? scrolled || open
      ? "bg-paper/90 border-navy-900/10 text-navy-900"
      : "bg-transparent border-transparent text-navy-900"
    : scrolled || open
      ? "bg-navy-900/85 border-white/10 text-ink"
      : "bg-transparent border-transparent text-ink";

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-colors duration-300 ${tone}`}
      >
        <div className="container-x flex h-16 items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => go("/")}
            className="flex min-h-[44px] items-center gap-2 font-en text-lg font-extrabold tracking-tight"
          >
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-teal-400" />
            {BRAND.en}
          </button>

          <nav className="hidden items-center gap-1 md:flex">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label}`}
                onClick={() => go(l.href)}
                className="min-h-[44px] rounded-lg px-3 text-sm font-medium opacity-80 transition hover:bg-teal-500/10 hover:opacity-100 hover:text-teal-400"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-book-cta"
              onClick={() => go("/#contact")}
              className="mr-2 flex min-h-[44px] items-center gap-2 rounded-xl bg-gold px-4 text-sm font-bold text-navy-950 transition hover:bg-gold-strong"
            >
              <CalendarCheck className="h-4 w-4" />
              احجز جلستك
            </button>
          </nav>

          <button
            data-testid="nav-hamburger"
            aria-label="فتح القائمة"
            onClick={() => setOpen(true)}
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-current/20 md:hidden"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      <BottomSheet open={open} onClose={() => setOpen(false)} label="قائمة التنقل" testid="nav-bottom-sheet">
        <div className="flex items-center justify-between pb-4">
          <span className="font-en text-base font-bold">{BRAND.en}</span>
          <button
            data-testid="nav-sheet-close"
            aria-label="إغلاق القائمة"
            onClick={() => setOpen(false)}
            className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/5"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex flex-col">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-sheet-link-${l.label}`}
              onClick={() => go(l.href)}
              className="min-h-[56px] border-b border-white/5 text-right text-lg font-semibold transition hover:text-teal-400"
            >
              {l.label}
            </button>
          ))}
        </nav>
        <button
          data-testid="nav-sheet-book-cta"
          onClick={() => go("/#contact")}
          className="mt-5 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-gold text-base font-bold text-navy-950"
        >
          <CalendarCheck className="h-5 w-5" />
          احجز جلستك الآن
        </button>
        <div className="mt-6 flex items-center justify-center gap-3">
          {SOCIALS.map((s) => (
            <a
              key={s.name}
              href={s.href}
              aria-label={s.name}
              data-testid={`nav-sheet-social-${s.name}`}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-white/5 text-ink-soft transition hover:text-teal-400"
            >
              <s.icon className="h-5 w-5" />
            </a>
          ))}
        </div>
      </BottomSheet>
    </>
  );
};

export default Navbar;
